// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import {
  intelligenceAdminApi,
  type ChatUseCaseDefault,
  type ChatUseCaseOverride,
  type ProviderDTO,
  type UpdateUseCaseOverrideRequest,
  type VoiceProfile,
} from '../../api/intelligenceAdminApi';
import VoiceProfileEditor from './VoiceProfileEditor';

interface Props {
  useCase: ChatUseCaseDefault;
  brokerageId: string;
  override: ChatUseCaseOverride;
  onClose: () => void;
  onSaved: () => void;
}

/** Each row in the override editor: an "Inherit from default" toggle that
 *  null-coalesces the override value, plus the field input itself. */
const OverrideEditModal: React.FC<Props> = ({ useCase, brokerageId, override, onClose, onSaved }) => {
  const [draft, setDraft] = useState<ChatUseCaseOverride>(override);
  const [providers, setProviders] = useState<ProviderDTO[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDraft(override); }, [override]);
  useEffect(() => {
    intelligenceAdminApi.listProviders().then(setProviders).catch(() => undefined);
  }, []);

  const inheritedAgentName = useCase.agentName ?? '';
  const inheritedPrompt = useCase.defaultSystemPrompt ?? '';
  const inheritedVoiceProfile = useCase.voiceProfile;
  const inheritedVoiceEnabled = useCase.voiceEnabled ?? false;
  const inheritedProvider = useCase.defaultLlmProvider;
  const inheritedModel = useCase.defaultModel;

  const update = (patch: Partial<ChatUseCaseOverride>) =>
    setDraft(prev => ({ ...prev, ...patch }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Build clear list: any override that is null/undefined goes into clearFields.
      const clear: string[] = [];
      const body: UpdateUseCaseOverrideRequest = {};
      const set = <K extends keyof UpdateUseCaseOverrideRequest>(
        key: K, value: UpdateUseCaseOverrideRequest[K], clearKey: string,
      ) => {
        if (value === null || value === undefined || value === '') clear.push(clearKey);
        else (body as Record<string, unknown>)[key as string] = value;
      };
      set('agentNameOverride', draft.agentNameOverride, 'agentNameOverride');
      set('personalityOverride', draft.personalityOverride, 'personalityOverride');
      set('systemPromptOverride', draft.systemPromptOverride, 'systemPromptOverride');
      set('llmProviderOverride', draft.llmProviderOverride, 'llmProviderOverride');
      set('modelOverride', draft.modelOverride, 'modelOverride');
      set('voiceProfileOverride', draft.voiceProfileOverride, 'voiceProfileOverride');
      if (draft.voiceEnabledOverride !== null && draft.voiceEnabledOverride !== undefined) {
        body.voiceEnabledOverride = draft.voiceEnabledOverride;
      } else {
        clear.push('voiceEnabledOverride');
      }
      if (draft.enabled !== null && draft.enabled !== undefined) body.enabled = draft.enabled;
      if (clear.length > 0) body.clearFields = clear;

      await intelligenceAdminApi.updateOverride(useCase.code, brokerageId, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const inherit = (label: string, value: string) => (
    <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
      ({label}: <code>{value || '∅'}</code>)
    </span>
  );

  const onVoiceChange = (next: { enabled: boolean; profile: VoiceProfile }) =>
    update({ voiceEnabledOverride: next.enabled, voiceProfileOverride: next.profile });

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Override for brokerage <code>{brokerageId}</code></h2>
          <button onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <h3>Persona</h3>
          <label>Agent name {inherit('default', inheritedAgentName)}
            <input type="text" maxLength={60} value={draft.agentNameOverride ?? ''}
              placeholder="(inherit)"
              onChange={(e) => update({ agentNameOverride: e.target.value || undefined })}
              style={input}/>
          </label>
          <label>Personality
            <textarea value={draft.personalityOverride ?? ''} maxLength={500}
              placeholder="(inherit)"
              onChange={(e) => update({ personalityOverride: e.target.value || undefined })}
              style={input} rows={2}/>
          </label>

          <h3>LLM</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>Provider {inherit('default', inheritedProvider)}
              <select value={draft.llmProviderOverride ?? ''}
                onChange={(e) => update({ llmProviderOverride: e.target.value || undefined })}
                style={input}>
                <option value="">(inherit)</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.display}{p.platformOnly ? ' (platform-only)' : ''}</option>
                ))}
              </select>
            </label>
            <label>Model {inherit('default', inheritedModel)}
              <input type="text" value={draft.modelOverride ?? ''} placeholder="(inherit)"
                onChange={(e) => update({ modelOverride: e.target.value || undefined })}
                style={input}/>
            </label>
          </div>

          <h3>System prompt</h3>
          <details style={{ marginBottom: 8 }}>
            <summary>Show platform-default prompt</summary>
            <pre style={{ fontSize: 12, background: '#f3f4f6', padding: 8, borderRadius: 4, whiteSpace: 'pre-wrap' }}>
              {inheritedPrompt}
            </pre>
          </details>
          <textarea value={draft.systemPromptOverride ?? ''} rows={8}
            placeholder="(inherit)"
            onChange={(e) => update({ systemPromptOverride: e.target.value || undefined })}
            style={{ ...input, fontFamily: 'monospace' }}/>

          <VoiceProfileEditor
            enabled={draft.voiceEnabledOverride ?? inheritedVoiceEnabled}
            profile={draft.voiceProfileOverride ?? inheritedVoiceProfile}
            agentName={draft.agentNameOverride ?? inheritedAgentName}
            onChange={onVoiceChange}/>

          {error && <div style={{ color: '#b91c1c', marginTop: 12 }}>{error}</div>}

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <button type="button" onClick={async () => {
              if (!confirm('Revert all overrides for this brokerage to defaults?')) return;
              await intelligenceAdminApi.deleteOverride(useCase.code, brokerageId);
              onSaved();
            }} style={{ color: '#b91c1c' }}>Revert all overrides</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ background: '#1e40af', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 4 }}>
                {submitting ? 'Saving…' : 'Save override'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  paddingTop: 40, zIndex: 1000, overflowY: 'auto',
};
const modal: React.CSSProperties = {
  background: 'white', padding: 24, borderRadius: 8, width: 720, maxWidth: '90vw',
};
const input: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: 4,
  border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px',
};

export default OverrideEditModal;
